/* Scrolling pitch graph (canvas): the singer's pitch trace over time, with an optional target band,
   target curve (siren) and hit marks. Driven imperatively through its handle at frame rate. */
import { type Ref, useEffect, useImperativeHandle, useRef } from 'react';
import { themeEvents } from '../core/theme';
import { mod, NATURALS, shortNote } from '../core/theory';

export type TraceState = 'good' | 'warn' | 'bad' | null | undefined;

export interface PitchGraphHandle {
  push(t: number, midi: number | null, state?: TraceState): void;
  setNow(t: number): void;
  setBand(midi: number | null, tol?: number): void;
  setCurve(fn: ((t: number) => number | null) | null, tol?: number): void;
  mark(t: number, midi: number, ok: boolean): void;
  setRange(lo: number, hi: number): void;
  clear(): void;
}

export interface PitchGraphProps {
  ref?: Ref<PitchGraphHandle>;
  lo?: number;
  hi?: number;
  seconds?: number;
  height?: number;
  /** horizontal position of "now" (0..1) */
  nowFrac?: number;
  labels?: boolean;
}

interface Colors {
  line: string;
  soft: string;
  muted: string;
  ink: string;
  good: string;
  warn: string;
  bad: string;
  mod: string;
  font: string;
}

export function PitchGraph({
  ref,
  lo = 48,
  hi = 72,
  seconds = 6,
  height = 220,
  nowFrac = 0.78,
  labels = true,
}: PitchGraphProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // All drawing state lives in a plain object so the render loop never depends on React.
  const s = useRef({
    lo,
    hi,
    curveTol: 50,
    pts: [] as { t: number; m: number | null; s: TraceState }[],
    band: null as { midi: number; tol: number } | null,
    curve: null as ((t: number) => number | null) | null,
    marks: [] as { t: number; midi: number; ok: boolean }[],
    now: 0,
    colors: null as Colors | null,
    W: 0,
  }).current;

  useImperativeHandle(
    ref,
    () => ({
      push(t, m, st) {
        s.pts.push({ t, m, s: st });
        s.now = Math.max(s.now, t);
        const cut = s.now - seconds * 1.2;
        while (s.pts.length && (s.pts[0] as { t: number }).t < cut) s.pts.shift();
      },
      setNow(t) {
        s.now = t;
      },
      setBand(midi, tol = 50) {
        s.band = midi == null ? null : { midi, tol };
      },
      setCurve(fn, tol) {
        s.curve = fn;
        if (tol) s.curveTol = tol;
      },
      mark(t, midi, ok) {
        s.marks.push({ t, midi, ok });
        if (s.marks.length > 40) s.marks.shift();
      },
      setRange(a, b) {
        s.lo = a;
        s.hi = b;
      },
      clear() {
        s.pts = [];
        s.marks = [];
      },
    }),
    [s, seconds],
  );

  useEffect(() => {
    const wrap = wrapRef.current;
    const cv = canvasRef.current;
    const ctx = cv?.getContext('2d');
    if (!wrap || !cv || !ctx) return;
    const H = height;
    const padL = labels ? 46 : 8;
    const padR = 8;
    const readColors = (): Colors => {
      const cs = getComputedStyle(wrap);
      const g = (n: string, d: string) => (cs.getPropertyValue(n) || d).trim();
      return {
        line: g('--line', '#ccc'),
        soft: g('--soft', '#eee'),
        muted: g('--muted', '#888'),
        ink: g('--ink', '#111'),
        good: g('--good', '#2a2'),
        warn: g('--warn', '#da2'),
        bad: g('--bad', '#d33'),
        mod: g('--mod', g('--accent', '#e80')),
        font: g('--font-mono', 'monospace'),
      };
    };
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      s.W = Math.max(200, wrap.clientWidth);
      cv.width = Math.round(s.W * dpr);
      cv.height = Math.round(H * dpr);
      cv.style.width = `${s.W}px`;
      cv.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const y = (m: number) => H - 8 - ((m - s.lo) / (s.hi - s.lo)) * (H - 16);
    const nowX = () => padL + (s.W - padL - padR) * nowFrac;
    const x = (t: number) => nowX() - ((s.now - t) / seconds) * (s.W - padL - padR);
    const traceColor = (c: Colors, st: TraceState) =>
      st === 'good' ? c.good : st === 'warn' ? c.warn : st === 'bad' ? c.bad : c.ink;
    const draw = () => {
      if (!s.colors) s.colors = readColors();
      const c = s.colors;
      const W = s.W;
      ctx.clearRect(0, 0, W, H);
      ctx.font = `11px ${c.font}`;
      ctx.textBaseline = 'middle';
      for (let m = Math.ceil(s.lo); m <= Math.floor(s.hi); m++) {
        const pc = mod(m, 12);
        const natural = NATURALS.includes(pc);
        ctx.strokeStyle = natural ? c.line : c.soft;
        ctx.lineWidth = pc === 0 ? 1.4 : 1;
        ctx.beginPath();
        ctx.moveTo(padL, y(m));
        ctx.lineTo(W - padR, y(m));
        ctx.stroke();
        if (labels && natural && (s.hi - s.lo <= 16 || pc === 0 || pc === 7 || pc === 4)) {
          ctx.fillStyle = pc === 0 ? c.ink : c.muted;
          ctx.fillText(shortNote(m), 4, y(m));
        }
      }
      if (s.band) {
        const tol = s.band.tol / 100;
        ctx.fillStyle = c.mod;
        ctx.globalAlpha = 0.16;
        ctx.fillRect(padL, y(s.band.midi + tol), W - padL - padR, y(s.band.midi - tol) - y(s.band.midi + tol));
        ctx.globalAlpha = 1;
        ctx.strokeStyle = c.mod;
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 5]);
        ctx.beginPath();
        ctx.moveTo(padL, y(s.band.midi));
        ctx.lineTo(W - padR, y(s.band.midi));
        ctx.stroke();
        ctx.setLineDash([]);
      }
      for (const mk of s.marks) {
        const xx = x(mk.t);
        if (xx < padL || xx > W) continue;
        ctx.fillStyle = mk.ok ? c.good : c.bad;
        ctx.beginPath();
        ctx.arc(xx, y(mk.midi), 4, 0, Math.PI * 2);
        ctx.fill();
      }
      if (s.curve) {
        const curve = s.curve;
        const tol = s.curveTol / 100;
        const t0 = s.now - seconds * nowFrac;
        const t1 = s.now + seconds * (1 - nowFrac);
        const step = seconds / 160;
        ctx.globalAlpha = 0.18;
        ctx.fillStyle = c.mod;
        ctx.beginPath();
        let first = true;
        for (let t = t0; t <= t1; t += step) {
          const m = curve(t);
          if (m == null) continue;
          if (first) {
            ctx.moveTo(x(t), y(m + tol));
            first = false;
          } else ctx.lineTo(x(t), y(m + tol));
        }
        for (let t = t1; t >= t0; t -= step) {
          const m = curve(t);
          if (m == null) continue;
          ctx.lineTo(x(t), y(m - tol));
        }
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = c.mod;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        first = true;
        for (let t = t0; t <= t1; t += step) {
          const m = curve(t);
          if (m == null) {
            first = true;
            continue;
          }
          if (first) {
            ctx.moveTo(x(t), y(m));
            first = false;
          } else ctx.lineTo(x(t), y(m));
        }
        ctx.stroke();
      }
      ctx.strokeStyle = c.muted;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(nowX(), 4);
      ctx.lineTo(nowX(), H - 4);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      let prev: { X: number; Y: number } | null = null;
      for (const p of s.pts) {
        if (p.m == null || p.m < s.lo - 1 || p.m > s.hi + 1) {
          prev = null;
          continue;
        }
        const X = x(p.t);
        const Y = y(p.m);
        if (X < padL) {
          prev = { X, Y };
          continue;
        }
        if (prev && Math.abs(prev.Y - Y) < 40) {
          ctx.strokeStyle = traceColor(c, p.s);
          ctx.beginPath();
          ctx.moveTo(prev.X, prev.Y);
          ctx.lineTo(X, Y);
          ctx.stroke();
        }
        prev = { X, Y };
      }
      const lastP = s.pts[s.pts.length - 1];
      if (lastP && lastP.m != null && s.now - lastP.t < 0.15) {
        ctx.fillStyle = traceColor(c, lastP.s);
        ctx.beginPath();
        ctx.arc(x(lastP.t), y(lastP.m), 5, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    let raf = 0;
    const frame = () => {
      draw();
      raf = requestAnimationFrame(frame);
    };
    s.colors = null;
    resize();
    raf = requestAnimationFrame(frame);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    ro?.observe(wrap);
    const offTheme = themeEvents.on('change', () => {
      s.colors = null;
    });
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      offTheme();
    };
  }, [s, height, labels, nowFrac, seconds]);

  return (
    <div className="pgraph" ref={wrapRef}>
      <canvas className="pg-canvas" ref={canvasRef} />
    </div>
  );
}
